import React, { useState, useEffect } from "react";
import moment from "moment";

// Components
import Sidebar from "../../components/Sidebar";
import Main from "./TreasuryDocuments/Main";
import FormAdjust from "./TreasuryDocuments/FormAdjust";
import FormTransfer from "./TreasuryDocuments/FormTransfer";
import FormWDP from "./TreasuryDocuments/FormWDP";

// Utils
import {
  getDataWithComp,
  getAlert,
  getMaxAdjNo,
  getMaxTnsNo,
  getMaxWDPNo,
} from "../../../utils/SamuiUtils";

const TreasuryDocuments = () => {
  const [mode, setMode] = useState("");
  console.debug("TreasuryDocuments mode", mode);
  const [dataMasterList, setDataMasterList] = useState([]);
  const [dataDetailList, setDataDetailList] = useState([]);
  const [maxAdjNo, setMaxAdjNo] = useState();
  const [maxTnsNo, setMaxTnsNo] = useState();
  const [maxWDPNo, setMaxWDPNo] = useState();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  useEffect(() => {
    initialize();
  }, []);

  const initialize = async () => {
    try {
      setMode("S");
      fetchRealtime(); // เรียกใช้งาน fetchRealtime เพื่อโหลดข้อมูลเมื่อ component โหลดครั้งแรก
    } catch (error) {
      getAlert("FAILED", error.message);
    }
  };

  const loadMasterListByDateRange = async ({ fromDate, toDate } = {}) => {
    const startDate = fromDate
      ? moment(fromDate).format("YYYY-MM-DD")
      : moment().startOf("month").format("YYYY-MM-DD");
    const endDate = toDate
      ? moment(toDate).format("YYYY-MM-DD")
      : moment().endOf("month").format("YYYY-MM-DD");

    const parseDocCreatedDate = (value) => {
      if (!value) return null;

      const normalizedValue = String(value)
        .trim()
        .replace(/([+-]\d{2}:?\d{2}|Z)$/i, "");

      const parsed = moment(normalizedValue, [
        "YYYY-MM-DD HH:mm:ss.SSS",
        "YYYY-MM-DD HH:mm:ss",
        "YYYY-MM-DDTHH:mm:ss.SSS",
        "YYYY-MM-DDTHH:mm:ss",
        "DD-MM-YYYY HH:mm:ss",
        "DD/MM/YYYY HH:mm:ss",
        "DD-MM-YYYY HH:mm",
        "DD/MM/YYYY HH:mm",
        "YYYY-MM-DD",
      ], true);

      if (parsed.isValid()) return parsed;

      const fallback = moment(normalizedValue);
      return fallback.isValid() ? fallback : null;
    };

    const getComparableDocDate = (row) => {
      return (
        parseDocCreatedDate(row?.WHDoc_CreatedDate) ||
        parseDocCreatedDate(row?.WHDoc_Date) ||
        parseDocCreatedDate(row?.WHDoc_TransDate)
      );
    };

    const [apiRows, rawRows, warehouses] = await Promise.all([
      getDataWithComp("API_1001_WHDOC_H", "ORDER BY WHDoc_No DESC"),
      getDataWithComp("WHDoc_H", "ORDER BY WHDoc_No DESC"),
      getDataWithComp("Tb_Set_WH", "AND WH_Status = 'Y'"),
    ]);

    const whNameMap = new Map(
      (Array.isArray(warehouses) ? warehouses : []).map((w) => [
        String(w.WH_Id),
        w.WH_Name,
      ])
    );

    const typeNameMap = {
      "1": "Adjust",
      "2": "Transfer",
      "3": "Withdraw",
    };

    const inRange = (row) => {
      const docDate = getComparableDocDate(row);
      if (!docDate || !docDate.isValid()) return false;

      const docDateOnly = docDate.format("YYYY-MM-DD");
      return docDateOnly >= startDate && docDateOnly <= endDate;
    };

    const normalizedApiRows = (Array.isArray(apiRows) ? apiRows : []).filter(inRange);

    const mergedMap = new Map(
      normalizedApiRows.map((row) => [
        String(row.WHDoc_Id ?? row.WHDoc_No),
        row,
      ])
    );

    (Array.isArray(rawRows) ? rawRows : []).filter(inRange).forEach((row) => {
      const key = String(row.WHDoc_Id ?? row.WHDoc_No);
      if (mergedMap.has(key)) return;

      mergedMap.set(key, {
        ...row,
        WHDocType_Name: row.WHDocType_Name || typeNameMap[String(row.WHDoc_Type)] || "-",
        F_WH_Name: row.F_WH_Name || whNameMap.get(String(row.F_WH_Id)) || "-",
        T_WH_Name: row.T_WH_Name || whNameMap.get(String(row.T_WH_Id)) || "-",
        WHDoc_CreatedBy: row.WHDoc_CreatedBy || row.Created_By || "-",
      });
    });

    const masterList = Array.from(mergedMap.values()).sort((a, b) =>
      String(b?.WHDoc_No || "").localeCompare(String(a?.WHDoc_No || ""), "th")
    );

    if (masterList && masterList.length > 0) {
      setDataMasterList(masterList);
    } else {
      setDataMasterList([]);
    }
  };

  const fetchRealtime = async () => {
    try {
      await loadMasterListByDateRange();

      // ⚡ Performance: Don't load detail list on initial load
      // Details will be fetched on-demand when user clicks a row in Main.js
      setDataDetailList([]);

      // หาค่าสูงของ WHDocNo ใน WHDOC_H สำหรับ ADJ (ปรับ) & TN (โอน)
      const findMaxAdjNo = await getDataWithComp(
        "WHDoc_H",
        `AND WHDoc_Type = '1' ORDER BY WHDoc_No DESC`
      );
      const maxAdj = getMaxAdjNo(findMaxAdjNo);

      const findMaxTnsNo = await getDataWithComp(
        "WHDoc_H",
        `AND WHDoc_Type = '2' ORDER BY WHDoc_No DESC`
      );
      const maxTns = getMaxTnsNo(findMaxTnsNo);

      const findMaxWDPNo = await getDataWithComp(
        "WHDoc_H",
        `AND WHDoc_Type = '3' ORDER BY WHDoc_No DESC`
      );
      const MaxWDP = getMaxWDPNo(findMaxWDPNo, "WDP");
      setMaxAdjNo(maxAdj);
      setMaxTnsNo(maxTns);
      setMaxWDPNo(MaxWDP);
    } catch (error) {
      getAlert("FAILED", error.message);
    }
  };

  const onChangeMode = (mode) => {
    console.debug("onChangeMode", mode);
    localStorage.setItem("TreasuryDocumentsMode", mode);
    setMode(mode);
  };

  return (
    <div>
      <div className="TreasuryDocuments">
        <button
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          className="btn btn-toggle-sidebar"
          style={{
            position: "fixed",
            top: "1rem",
            left: isSidebarOpen ? "190px" : "-15px",
            zIndex: 1001,
            background: "transparent",
            border: "none",
            color: "#EF6C00",
            fontSize: "20px",
          }}
        >
          {isSidebarOpen ? "✖" : "☰"}
        </button>
        <div className="wrapper">
          <Sidebar
            isSidebarOpen={isSidebarOpen}
            setIsSidebarOpen={setIsSidebarOpen}
          />
          <div
            className={`main-panel ${
              isSidebarOpen ? "sidebar-open" : "sidebar-closed"
            }`}
          >
            <div className="container">
              <div className="page-inner">
                {mode === "S" && (
                  <Main
                    name={"จัดการเอกสารงานคลัง"}
                    onChangeMode={onChangeMode}
                    dataMasterList={dataMasterList}
                    dataDetailList={dataDetailList}
                    onSearchDateRange={loadMasterListByDateRange}
                  />
                )}
                {mode === "FA" && (
                  <FormAdjust
                    callInitialize={initialize}
                    mode={mode}
                    name={"ใบปรับปรุงสินค้า"}
                    whDocNo={maxAdjNo}
                  />
                )}
                {mode === "FT" && (
                  <FormTransfer
                    callInitialize={initialize}
                    mode={mode}
                    name={"ใบโอนย้ายสินค้า"}
                    whDocNo={maxTnsNo}
                  />
                )}
                {mode === "WDP" && (
                  <FormWDP
                    callInitialize={initialize}
                    mode={mode}
                    name={"ใบเบิกสินค้า"}
                    whDocNo={maxWDPNo}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TreasuryDocuments;
